import React from 'react';
import type { ApprovalStageWizardProps, StageData, Rule } from './types';
import { WizardSidebarResponsive } from './WizardSidebarResponsive';
import { WizardFooterSticky } from './WizardFooterSticky';
import { Step1BasicDetails } from './Step1BasicDetailsCompact';
import { Step3SmartRules } from './Step3SmartRulesTabs';
import { Step4Notifications } from './Step4NotificationsCompact';
import { RuleBuilderDialog } from './RuleBuilderDialog';
import { getInitialStageData, getAllCompletedSteps, canProceedToNextStep, migrateStageData } from './utils';
import { toast } from 'sonner';
import {
  ApprovalModal,
  ApprovalModalBody,
} from '../shared/approval';

export const ApprovalStageWizard: React.FC<ApprovalStageWizardProps> = ({
  isOpen,
  onClose,
  onSave,
  onSaveDraft: onSaveDraftProp,
  existingStage,
  workflowContext,
  onSaveAndAddAnother,
  initialStageName,
}) => {
  const [currentStep, setCurrentStep] = React.useState(1);
  const [stageData, setStageData] = React.useState<StageData>(getInitialStageData());
  const [completedSteps, setCompletedSteps] = React.useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [lastAutoSave, setLastAutoSave] = React.useState<Date | null>(null);

  // Rule Builder Dialog State
  const [isRuleBuilderOpen, setIsRuleBuilderOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<Rule | undefined>(undefined);
  const [ruleExecutionTiming, setRuleExecutionTiming] = React.useState<'pre-approval' | 'post-approval'>('pre-approval');
  const [ruleContext, setRuleContext] = React.useState<'approved' | 'rejected' | undefined>(undefined);

  // Initialize with existing stage data if editing
  React.useEffect(() => {
    if (existingStage) {
      // Migrate any legacy data (e.g., 'conditional' type)
      const migratedStage = migrateStageData(existingStage);
      setStageData(migratedStage);
      setCompletedSteps(getAllCompletedSteps(migratedStage));
    } else {
      const initialData = getInitialStageData();
      // Pre-fill stage name if provided (e.g., from placeholder creation)
      if (initialStageName) {
        initialData.basicDetails.stageName = initialStageName;
      }
      setStageData(initialData);
      setCompletedSteps(new Set());
      
      // Try to restore draft on new stage creation
      if (isOpen && !existingStage) {
        // First check for cancelled draft (more recent)
        const cancelledDraftKey = `workflow-stage-cancelled-${workflowContext?.workflowId || 'new'}`;
        const cancelledDraft = localStorage.getItem(cancelledDraftKey);
        
        if (cancelledDraft) {
          try {
            const { stageData: draftData, timestamp } = JSON.parse(cancelledDraft);
            const draftAge = Date.now() - new Date(timestamp).getTime();
            
            // Only offer to restore recent cancellations (< 1 hour)
            if (draftAge < 3600000) {
              const shouldRestore = window.confirm(
                'You cancelled a stage configuration recently. Would you like to restore it?'
              );
              
              if (shouldRestore) {
                setStageData(draftData);
                toast.success('Previous configuration restored');
              }
              
              // Clean up after offering restore
              localStorage.removeItem(cancelledDraftKey);
              return;
            } else {
              localStorage.removeItem(cancelledDraftKey);
            }
          } catch (error) {
            console.error('Failed to restore cancelled draft:', error);
          }
        }
        
        // Then check for regular auto-saved draft
        const draftKey = `workflow-stage-draft-${workflowContext?.workflowId || 'new'}`;
        const savedDraft = localStorage.getItem(draftKey);
        
        if (savedDraft) {
          try {
            const { stageData: draftData, timestamp } = JSON.parse(savedDraft);
            const draftAge = Date.now() - new Date(timestamp).getTime();
            
            // Only restore drafts less than 24 hours old
            if (draftAge < 86400000) {
              const shouldRestore = window.confirm('A draft was found. Would you like to restore it?');
              if (shouldRestore) {
                setStageData(draftData);
                toast.success('Draft restored');
                return;
              } else {
                localStorage.removeItem(draftKey);
              }
            } else {
              // Clean up old drafts
              localStorage.removeItem(draftKey);
            }
          } catch (error) {
            console.error('Failed to restore draft:', error);
          }
        }
      }
    }
    setCurrentStep(1);
    setIsDirty(false);
  }, [existingStage, isOpen, workflowContext, initialStageName]);

  // Update completed steps when stage data changes
  React.useEffect(() => {
    setCompletedSteps(getAllCompletedSteps(stageData));
  }, [stageData]);

  // Auto-save draft every 2 minutes
  React.useEffect(() => {
    if (!isDirty || !isOpen) return;

    const AUTO_SAVE_INTERVAL = 120000; // 2 minutes
    const timer = setInterval(() => {
      handleAutoSave();
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(timer);
  }, [isDirty, isOpen, stageData]);

  // Browser beforeunload warning for unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && isOpen) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isOpen]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // ESC to close/cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
      
      // Ctrl+S (or Cmd+S on Mac) to save draft
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveDraft();
      }
      
      // Ctrl+Enter (or Cmd+Enter on Mac) to complete (only on final step)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && currentStep === 3) {
        e.preventDefault();
        handleComplete();
      }
      
      // Arrow keys for navigation (only when not in input fields)
      const target = e.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
      
      if (!isInputField) {
        if (e.key === 'ArrowLeft' && currentStep > 1) {
          e.preventDefault();
          handlePrevious();
        }
        
        if (e.key === 'ArrowRight' && currentStep < 3) {
          e.preventDefault();
          handleNext();
        }
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, currentStep, isDirty]);

  const handleStepClick = (step: number) => {
    setCurrentStep(step);
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      if (canProceedToNextStep(currentStep, stageData)) {
        setCurrentStep(currentStep + 1);
      } else {
        toast.error('Please complete all required fields before proceeding.');
      }
    }
  };

  const handleAutoSave = async () => {
    try {
      const draftKey = `workflow-stage-draft-${workflowContext?.workflowId || 'new'}`;
      localStorage.setItem(draftKey, JSON.stringify({
        stageData,
        timestamp: new Date().toISOString(),
      }));
      
      setLastAutoSave(new Date());
      setIsDirty(false);
      
      // Show subtle notification
      toast.success('Draft auto-saved', {
        duration: 2000,
        icon: '💾',
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error to user for auto-save failures
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    
    try {
      // If onSaveDraft prop is provided, call it to persist to backend
      if (onSaveDraftProp) {
        await onSaveDraftProp(stageData);
        toast.success('Draft saved to server!');
        setIsDirty(false);
        setLastAutoSave(new Date());
      } else {
        // Fallback to localStorage if no backend save handler provided
        const draftKey = `workflow-stage-draft-${workflowContext?.workflowId || 'new'}`;
        localStorage.setItem(draftKey, JSON.stringify({
          stageData,
          timestamp: new Date().toISOString(),
        }));
        toast.success('Draft saved locally!');
        setIsDirty(false);
        setLastAutoSave(new Date());
      }
    } catch (error) {
      console.error('Save draft failed:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = () => {
    // Validate all required steps
    const requiredSteps = [1, 2];
    const incompleteSteps = requiredSteps.filter((step) => !completedSteps.has(step));

    if (incompleteSteps.length > 0) {
      toast.error(`Please complete steps: ${incompleteSteps.join(', ')}`);
      return;
    }

    onSave(stageData);
    
    // Clean up draft after successful save
    const draftKey = `workflow-stage-draft-${workflowContext?.workflowId || 'new'}`;
    localStorage.removeItem(draftKey);
    
    toast.success('Approval stage has been configured successfully!');
    onClose();
  };

  const handleCompleteAndAddAnother = () => {
    // Validate all required steps
    const requiredSteps = [1, 2];
    const incompleteSteps = requiredSteps.filter((step) => !completedSteps.has(step));

    if (incompleteSteps.length > 0) {
      toast.error(`Please complete steps: ${incompleteSteps.join(', ')}`);
      return;
    }

    // Save the current stage
    onSave(stageData);
    
    // Clean up draft after successful save
    const draftKey = `workflow-stage-draft-${workflowContext?.workflowId || 'new'}`;
    localStorage.removeItem(draftKey);
    
    // Reset wizard for new stage
    setStageData(getInitialStageData());
    setCompletedSteps(new Set());
    setCurrentStep(1);
    setIsDirty(false);
    
    // Call the parent to create a new stage and keep wizard open
    if (onSaveAndAddAnother) {
      onSaveAndAddAnother();
    }
    
    // Show success message
    toast.success('Stage saved! Ready to add another stage.', {
      duration: 3000,
      icon: '✅',
    });
  };

  const handleClose = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
      
      // Store as "cancelled draft" for potential restore
      const cancelledDraftKey = `workflow-stage-cancelled-${workflowContext?.workflowId || 'new'}`;
      try {
        localStorage.setItem(cancelledDraftKey, JSON.stringify({
          stageData,
          timestamp: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('Failed to store cancelled draft:', error);
      }
    }
    onClose();
  };

  // Update stage data and mark as dirty
  const updateStageData = (updates: Partial<StageData>) => {
    setStageData((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  };

  // Rule Builder Handlers
  const handleOpenRuleBuilder = (
    executionTiming: 'pre-approval' | 'post-approval',
    context?: 'approved' | 'rejected',
    rule?: Rule
  ) => {
    setRuleExecutionTiming(executionTiming);
    setRuleContext(context);
    setEditingRule(rule);
    setIsRuleBuilderOpen(true);
  };

  const handleSaveRule = (rule: Rule) => {
    const existingRules = stageData.smartRules.rules || [];
    const updatedRules = editingRule
      ? existingRules.map((r) => (r.id === rule.id ? rule : r))
      : [...existingRules, rule];

    updateStageData({
      smartRules: {
        ...stageData.smartRules,
        rules: updatedRules,
      },
    });

    setIsRuleBuilderOpen(false);
    setEditingRule(undefined);
  };

  const handleDeleteRule = (ruleId: string) => {
    updateStageData({
      smartRules: {
        ...stageData.smartRules,
        rules: stageData.smartRules.rules.filter((r) => r.id !== ruleId),
      },
    });
  };

  const handleReorderRule = (ruleId: string, direction: 'up' | 'down') => {
    const rules = [...stageData.smartRules.rules];
    const index = rules.findIndex((r) => r.id === ruleId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;

    [rules[index], rules[newIndex]] = [rules[newIndex], rules[index]];

    // Update order numbers
    rules.forEach((rule, idx) => {
      rule.order = idx + 1;
    });

    updateStageData({
      smartRules: {
        ...stageData.smartRules,
        rules,
      },
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicDetails
            basicDetails={stageData.basicDetails}
            approver={stageData.approver}
            allowApproverEdit={stageData.allowApproverEdit}
            onBasicDetailsChange={(data) => updateStageData({ basicDetails: data })}
            onApproverChange={(data) => updateStageData({ approver: data })}
            onAllowApproverEditChange={(value) => updateStageData({ allowApproverEdit: value })}
          />
        );
      case 2:
        return (
          <Step3SmartRules
            data={stageData.smartRules}
            onChange={(data) => updateStageData({ smartRules: data })}
            availableStages={workflowContext.allStages}
            workflowContext={workflowContext}
            onAddRule={(executionTiming, context) => handleOpenRuleBuilder(executionTiming, context)}
            onEditRule={(rule) => handleOpenRuleBuilder(rule.executionTiming, rule.context, rule)}
            onDeleteRule={(ruleId) => handleDeleteRule(ruleId)}
            onReorderRule={(ruleId, direction) => handleReorderRule(ruleId, direction)}
          />
        );
      case 3:
        return (
          <Step4Notifications
            data={stageData.notifications}
            onChange={(data) => updateStageData({ notifications: data })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ApprovalModal
        isOpen={isOpen}
        onClose={handleClose}
        title={existingStage ? 'Edit Approval Stage' : 'Configure Approval Stage'}
        description={
          existingStage
            ? 'Update the configuration for this approval stage'
            : 'Set up a new approval stage for your workflow'
        }
        size="full"
        showCloseButton={false}
      >
        <ApprovalModalBody className="p-0 h-screen flex flex-col overflow-hidden">

          {/* Workflow Context Breadcrumb */}
          {workflowContext && (workflowContext.workflowName || workflowContext.totalStages > 0) && (
            <div className="flex-shrink-0 border-b border-border bg-muted/30 px-4 py-2">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {workflowContext.workflowName || 'New Workflow'}
                </span>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar */}
            <WizardSidebarResponsive
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
            />

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-accent/5">
              <div className="max-w-4xl mx-auto p-4 pb-24">
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  {renderStepContent()}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <WizardFooterSticky
            currentStep={currentStep}
            totalSteps={3}
            canProceed={canProceedToNextStep(currentStep, stageData)}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSaveDraft={handleSaveDraft}
            onComplete={handleComplete}
            onCompleteAndAddAnother={!existingStage ? handleCompleteAndAddAnother : undefined}
            onCancel={handleClose}
            isSaving={isSaving}
          />
        </ApprovalModalBody>
      </ApprovalModal>

      {/* Rule Builder Dialog */}
      <RuleBuilderDialog
        isOpen={isRuleBuilderOpen}
        onClose={() => {
          setIsRuleBuilderOpen(false);
          setEditingRule(undefined);
        }}
        onSave={handleSaveRule}
        existingRule={editingRule}
        executionTiming={ruleExecutionTiming}
        context={ruleContext}
        availableStages={workflowContext.allStages}
        workflowContext={workflowContext}
      />
    </>
  );
};
